package dev.kuml.portal.web.editor

/**
 * "What does the inspector panel show right now" -- a pure function of [SelectionState] +
 * [DiagramOutlineResponse], entirely DOM-free (R-27, kUML Portal "Interactive Diagram-Editing UI"
 * wave). `InspectorPanel.kt` renders exactly one of these variants; it never re-derives selection
 * logic itself.
 *
 * Mirrors the design-team ruling verbatim: nothing selected -> the panel is invisible; one node ->
 * its full editing form (name/visibility/abstract/attributes/operations/relationships); two nodes ->
 * ONLY a "create association" affordance, nothing else; one edge -> its editing form (name/both
 * ends' role+multiplicity+navigable/delete). There is no variant for three or more nodes -- see
 * [InspectorModel.contentFor]'s KDoc for how that case degrades.
 */
public sealed interface InspectorContent {
    public data object None : InspectorContent

    /** @property relations Every relationship touching [element] (see
     *   [dev.kuml.portal.web.editor.relationsTouching]) -- the panel's "Beziehungen" list; clicking
     *   an entry there selects that edge (there is no SVG id to click directly, see K-3/K-4). */
    public data class Node(
        val element: OutlineElement,
        val relations: List<OutlineElement>,
    ) : InspectorContent

    /** The two-node "create association" affordance -- no other editing surface for this state. */
    public data class TwoNodes(
        val first: OutlineElement,
        val second: OutlineElement,
    ) : InspectorContent

    public data class Edge(
        val element: OutlineElement,
    ) : InspectorContent
}

public object InspectorModel {
    /**
     * Degrades gracefully for a [SelectionState] the outline can no longer resolve as a NODE
     * selection (a stale id that slipped past [DiagramSelection.restore], or a selected node id
     * that has since become a relationship's id via a race that should not really happen but is
     * cheap to guard) to [InspectorContent.None] rather than throwing -- an inspector panel that
     * briefly goes blank on a race is a far better failure mode than one that crashes the whole
     * screen.
     *
     * **Review fix (MINOR, 2026-09-02):** an earlier revision looked up a selected node id via
     * plain [elementById], with no [kind][OutlineElement.kind] check at all -- so a `nodeIds`
     * entry that had since become a relationship's id (the very race this KDoc already claimed to
     * guard against) resolved to `InspectorContent.Node(element = <the relationship>, relations =
     * emptyList())` instead of degrading to [InspectorContent.None], rendering a classifier-editing
     * form over what is actually an association/generalization/etc. Every `nodeIds` entry is now
     * checked against [nodeIdsOf] FIRST, in both the one-node and two-node branches below.
     *
     * **Review fix, round 2 (MINOR / Spiegel-Lücke, 2026-09-02):** the fix above only guarded the
     * two NODE branches -- the EDGE branch below still resolved [selection]'s `edgeId` via plain
     * [elementById] with no [kind][OutlineElement.kind] check, the exact mirror image of the bug
     * just described: an `edgeId` that has since become a CLASSIFIER's id (e.g. a deleted
     * association's id reused, after a rename, by a class -- see [UmlIds.disambiguate]'s id
     * derivation) resolved to `InspectorContent.Edge(element = <the classifier>)`, rendering the
     * relationship-editing form (name, both ends' role/multiplicity/navigable, delete) over a
     * classifier. `edgeId` is now checked against [relationshipIdsOf] FIRST, exactly like `nodeIds`
     * is checked against [nodeIdsOf] above.
     *
     * Three-or-more selected nodes (reachable only via repeated shift-click, see
     * [DiagramSelection.shiftClick]) degrade to [InspectorContent.TwoNodes] over the FIRST two
     * selected (click order, not id order) -- the panel offers the same "create association"
     * affordance a plain two-node selection would, silently ignoring the extra nodes rather than
     * inventing a UI this wave's design review never specified for that case.
     */
    public fun contentFor(
        selection: SelectionState,
        outline: DiagramOutlineResponse,
    ): InspectorContent {
        val edgeId = selection.edgeId
        if (edgeId != null) {
            if (edgeId !in relationshipIdsOf(outline)) return InspectorContent.None
            val edge = elementById(outline, edgeId) ?: return InspectorContent.None
            return InspectorContent.Edge(edge)
        }
        val nodeIds = nodeIdsOf(outline)
        return when {
            selection.nodeIds.isEmpty() -> InspectorContent.None
            selection.nodeIds.size == 1 -> {
                val id = selection.nodeIds[0]
                if (id !in nodeIds) return InspectorContent.None
                val node = elementById(outline, id) ?: return InspectorContent.None
                InspectorContent.Node(node, relationsTouching(outline, node.id))
            }
            else -> {
                val firstId = selection.nodeIds[0]
                val secondId = selection.nodeIds[1]
                if (firstId !in nodeIds || secondId !in nodeIds) return InspectorContent.None
                val first = elementById(outline, firstId) ?: return InspectorContent.None
                val second = elementById(outline, secondId) ?: return InspectorContent.None
                InspectorContent.TwoNodes(first, second)
            }
        }
    }
}
